import { observer } from 'mobx-react-lite';
import { updateTodoDueDate, updateTodoReminder } from 'owa-todo-actions';
import getTodoContextMenuItems from '../utils/getTodoContextMenuItems';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import { InlineDatePicker } from 'owa-datepicker';
import { DateTimePicker } from 'owa-datetimepicker';
import { observableNow } from 'owa-observable-datetime';
import { getDefaultReminderDateTime } from 'owa-todo-utils';
import * as React from 'react';
import { getTodoActionTelemetryData } from '../utils/getTodoActionTelemetryData';
import type TodoContextMenuState from '../schema/TodoContextMenuState';
import type { ContextMenuTodoActionTelemetryData } from '../schema/ContextMenuTodoActionTelemetryData';
import type { Todo } from 'owa-todo-types';
import type { OwaDate } from 'owa-datetime';
import type { TodoContextMenuCallbacks } from '../schema/TodoContextMenuCallbacks';

export interface TodoContextMenuProps {
    /**
     * contextMenuState
     *
     * @type TodoContextMenuState
     */
    contextMenuState: TodoContextMenuState;

    /**
     * callbacks for some todo context menu actions
     *
     * @type TodoContextMenuCallbacks
     */
    callbacks: TodoContextMenuCallbacks;

    /**
     * The todo upon which the context menu was triggered
     *
     * @type Todo
     */
    todo: Todo;

    /**
     * telemetry data which will be dispatched with todo actions
     * taken from the context menu.
     *
     * @type ContextMenuTodoActionTelemetryData
     */
    todoActionTelemetryData: ContextMenuTodoActionTelemetryData;

    /**
     * OPTIONAL Callback to allow consumers to provide custom contextual menu items to appear
     * in the todo context menu
     *
     * @type (todoId: string) => IContextualMenuItem[]
     */
    getCustomContextMenuItems?: (todoId: string) => IContextualMenuItem[];
}

export default observer(function TodoContextMenu(props: TodoContextMenuProps) {
    const {
        contextMenuState,
        todo,
        todoActionTelemetryData,
        callbacks,
        getCustomContextMenuItems,
    } = props;
    const { hideTodoContextMenu } = callbacks;
    const onReminderSave = React.useCallback(
        (selectedReminderTime: OwaDate) => {
            updateTodoReminder(
                contextMenuState.todoId,
                selectedReminderTime,
                getTodoActionTelemetryData(todoActionTelemetryData)
            );
            hideTodoContextMenu();
        },
        [contextMenuState, todoActionTelemetryData, hideTodoContextMenu]
    );
    const onRenderReminderPicker = React.useCallback(() => {
        const reminderTime = todo.ReminderDateTime
            ? todo.ReminderDateTime
            : getDefaultReminderDateTime(observableNow());
        return renderReminderPicker(
            reminderTime /* intialTime */,
            onReminderSave /* onSave */,
            hideTodoContextMenu /* onCancel */
        );
    }, [hideTodoContextMenu, todo]);
    const onDueDateSave = React.useCallback(
        (selectedDueDateTime: OwaDate) => {
            updateTodoDueDate(
                contextMenuState.todoId,
                selectedDueDateTime,
                getTodoActionTelemetryData(todoActionTelemetryData)
            );
            hideTodoContextMenu();
        },
        [contextMenuState, todoActionTelemetryData, hideTodoContextMenu]
    );
    const onRenderDueDatePicker = React.useCallback(() => {
        const dueDate = todo.DueDateTime
            ? todo.DueDateTime
            : getDefaultReminderDateTime(observableNow());
        return renderDueDatePicker(dueDate /*initialDate*/, onDueDateSave /*onSave*/);
    }, [todo]);

    return (
        <ContextualMenu
            shouldFocusOnMount={true}
            target={contextMenuState.anchor}
            directionalHint={DirectionalHint.bottomLeftEdge}
            onDismiss={hideTodoContextMenu}
            items={getTodoContextMenuItems(
                contextMenuState.todoId,
                todo,
                todoActionTelemetryData,
                callbacks,
                onRenderReminderPicker,
                onRenderDueDatePicker,
                getCustomContextMenuItems
            )}
            calloutProps={{ preventDismissOnScroll: true }}
        />
    );
});

function renderReminderPicker(
    initialDate: OwaDate,
    onSave: (selectedReminderTime: OwaDate) => void,
    onCancel: () => void
): JSX.Element {
    return <DateTimePicker initialValue={initialDate} onSave={onSave} onCancel={onCancel} />;
}

function renderDueDatePicker(initialDate: OwaDate, onSave: (date: OwaDate) => void): JSX.Element {
    return (
        <InlineDatePicker
            value={initialDate}
            highlightCurrentMonth={false}
            highlightSelectedMonth={true}
            isDayPickerVisible={true}
            showGoToToday={false}
            onSelectDate={onSave}
        />
    );
}
